import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Package,
  DollarSign,
  Image as ImageIcon,
  Tag,
  Warehouse,
  AlertTriangle,
  Save,
  X,
  Plus,
  Minus,
  Loader2,
  Upload,
  Star,
  Sparkles,
  Settings,
  Eye,
  ShoppingCart,
  Truck,
  Clock,
  CheckCircle2,
  Globe
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CategorySelect } from './CategorySelect';
import { BrandSelect } from './BrandSelect';
import { ImageUpload } from './ImageUpload';
import { VariationsManager } from './VariationsManager';
import { Product, ProductVariation } from '../types';
import { createProduct, updateProduct, validateProductData } from '../services/productService';
import { 
  moveImagesToProduct, 
  deleteImagesByUrls 
} from '../services/imageService';
import { useAIProductData } from '../hooks/useAIProductData';
import { AIProductRequest } from '../services/aiProductService';

interface ProductFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedProduct?: Product) => void;
  product?: Product | null;
  mode: 'add' | 'edit';
  cloneSource?: Product | null;
}

interface FormData {
  name: string;
  description: string;
  price: number;
  jamaicanPrice: number | null;
  onSalePrice: number | null;
  image: string;
  images: string[];
  category: string;
  brand: string;
  tags: string[];
  isNew: boolean;
  isBestSeller: boolean;
  onSale: boolean;
  disabled: boolean;
  stockQuantity: number;
  lowStockThreshold: number;
  netWeight: number | null;
  weightUnit: 'g' | 'kg' | 'oz' | 'lb' | 'ml';
  listingQuantity: number;
  seoTitle: string;
  seoDescription: string;
  availableShippingOptions: string[];
  variations: ProductVariation[];
  // EasyPost customs fields
  manufacturer: string;
  hsCode: string;
  originCountry: string;
  commodityCodes: {
    US: string;
    UK: string;
    CA: string;
    EU: string;
  };
}

const defaultFormData: FormData = {
  name: '',
  description: '',
  price: 0,
  jamaicanPrice: null,
  onSalePrice: null,
  image: '',
  images: [],
  category: '',
  brand: '',
  tags: [],
  isNew: false,
  isBestSeller: false,
  onSale: false,
  disabled: true,
  stockQuantity: 0,
  lowStockThreshold: 10,
  netWeight: null,
  weightUnit: 'g',
  listingQuantity: 1,
  seoTitle: '',
  seoDescription: '',
  availableShippingOptions: [],
  variations: [],
  // EasyPost customs fields
  manufacturer: '',
  hsCode: '',
  originCountry: '',
  commodityCodes: {
    US: '',
    UK: '',
    CA: '',
    EU: '',
  },
};

export function ProductForm({ isOpen, onClose, onSuccess, product, mode, cloneSource }: ProductFormProps) {
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [newImage, setNewImage] = useState('');
  const [originalImages, setOriginalImages] = useState<string[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [availableShippingTiers, setAvailableShippingTiers] = useState<Array<{id: string, name: string, description: string}>>([]);
  const [loadingShippingTiers, setLoadingShippingTiers] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  
  // AI Product Data Hook
  const aiProductData = useAIProductData();

  // Helper function to create AI request data from current form
  const createAIRequestData = (): AIProductRequest => ({
    name: formData.name,
    category: formData.category,
    brand: formData.brand,
  });

  // AI Auto-population handlers
  const handleAIManufacturer = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a product name first');
      return;
    }

    const requestData = createAIRequestData();
    const result = await aiProductData.actions.getManufacturer(requestData);
    
    if (result && aiProductData.utils.isConfidenceAcceptable(result.confidence)) {
      handleInputChange('manufacturer', result.value);
      toast.success(`Manufacturer auto-populated: ${result.value} (${result.confidence}% confidence)`);
    } else if (result) {
      toast.warning(`Low confidence result: ${result.value} (${result.confidence}% confidence). Please verify.`);
      handleInputChange('manufacturer', result.value);
    }
  };

  const handleAIOriginCountry = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a product name first');
      return;
    }

    const requestData = createAIRequestData();
    const result = await aiProductData.actions.getOriginCountry(requestData, formData.manufacturer);
    
    if (result && aiProductData.utils.isConfidenceAcceptable(result.confidence)) {
      handleInputChange('originCountry', result.value);
      toast.success(`Origin country auto-populated: ${result.value} (${result.confidence}% confidence)`);
    } else if (result) {
      toast.warning(`Low confidence result: ${result.value} (${result.confidence}% confidence). Please verify.`);
      handleInputChange('originCountry', result.value);
    }
  };

  const handleAIHSCode = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a product name first');
      return;
    }

    const requestData = createAIRequestData();
    const result = await aiProductData.actions.getHSCode(requestData);
    
    if (result && aiProductData.utils.isConfidenceAcceptable(result.confidence)) {
      handleInputChange('hsCode', result.value);
      toast.success(`HS Code auto-populated: ${result.value} (${result.confidence}% confidence)`);
    } else if (result) {
      toast.warning(`Low confidence result: ${result.value} (${result.confidence}% confidence). Please verify.`);
      handleInputChange('hsCode', result.value);
    }
  };

  const handleAIBatchFill = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a product name first');
      return;
    }

    const requestData = createAIRequestData();
    const result = await aiProductData.actions.getAllData(requestData);
    
    if (result) {
      // Update all fields with AI results
      handleInputChange('manufacturer', result.manufacturer.value);
      handleInputChange('originCountry', result.originCountry.value);
      handleInputChange('hsCode', result.hsCode.value);

      const confidenceSummary = aiProductData.computed.getConfidenceSummary();
      if (confidenceSummary) {
        toast.success(`All fields auto-populated! Average confidence: ${confidenceSummary.average}%`);
      } else {
        toast.success('All fields auto-populated with AI data');
      }
    }
  };

  // Initialize form data when product changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (product && mode === 'edit') {
        const productFormData = {
          name: product.name || '',
          description: product.description || '',
          price: product.price || 0,
          jamaicanPrice: product.jamaicanPrice ?? null,
          onSalePrice: product.onSalePrice || null,
          image: product.image || '',
          images: product.images || [],
          category: product.category || '',
          brand: product.brand || '',
          tags: product.tags || [],
          isNew: product.isNew || false,
          isBestSeller: product.isBestSeller || false,
          onSale: product.onSale || false,
          disabled: product.disabled || false,
          stockQuantity: product.stockQuantity || 0,
          lowStockThreshold: product.lowStockThreshold || 10,
          netWeight: product.netWeight !== undefined ? product.netWeight : null,
          weightUnit: product.weightUnit || 'g',
          listingQuantity: product.listingQuantity !== undefined ? product.listingQuantity : 1,
          seoTitle: product.seoTitle || '',
          seoDescription: product.seoDescription || '',
          availableShippingOptions: product.availableShippingOptions || [],
          variations: product.variations || [],
          // EasyPost customs fields
          manufacturer: product.manufacturer || '',
          hsCode: product.hsCode || '',
          originCountry: product.originCountry || '',
          commodityCodes: {
            US: product.commodityCodes?.US || '',
            UK: product.commodityCodes?.UK || '',
            CA: product.commodityCodes?.CA || '',
            EU: product.commodityCodes?.EU || '',
          },
        };
        setFormData(productFormData);
        setOriginalImages(product.images || []);
      } else if (mode === 'add' && cloneSource) {
        const cloneData = {
          name: cloneSource.name ? `${cloneSource.name} (Copy)` : '',
          description: cloneSource.description || '',
          price: cloneSource.price || 0,
          jamaicanPrice: cloneSource.jamaicanPrice ?? null,
          onSalePrice: cloneSource.onSalePrice || null,
          image: cloneSource.image || '',
          images: cloneSource.images || [],
          category: cloneSource.category || '',
          brand: cloneSource.brand || '',
          tags: cloneSource.tags || [],
          isNew: cloneSource.isNew || false,
          isBestSeller: cloneSource.isBestSeller || false,
          onSale: cloneSource.onSale || false,
          disabled: cloneSource.disabled ?? true,
          stockQuantity: cloneSource.stockQuantity || 0,
          lowStockThreshold: cloneSource.lowStockThreshold || 10,
          netWeight: cloneSource.netWeight !== undefined ? cloneSource.netWeight : null,
          weightUnit: cloneSource.weightUnit || 'g',
          listingQuantity: cloneSource.listingQuantity !== undefined ? cloneSource.listingQuantity : 1,
          seoTitle: cloneSource.seoTitle || '',
          seoDescription: cloneSource.seoDescription || '',
          availableShippingOptions: cloneSource.availableShippingOptions || [],
          variations: cloneSource.variations || [],
          // EasyPost customs fields
          manufacturer: cloneSource.manufacturer || '',
          hsCode: cloneSource.hsCode || '',
          originCountry: cloneSource.originCountry || '',
          commodityCodes: {
            US: cloneSource.commodityCodes?.US || '',
            UK: cloneSource.commodityCodes?.UK || '',
            CA: cloneSource.commodityCodes?.CA || '',
            EU: cloneSource.commodityCodes?.EU || '',
          },
        };
        setFormData(cloneData);
        setOriginalImages(cloneSource.images || []);
      } else if (mode === 'add') {
        setFormData(defaultFormData);
        setOriginalImages([]);
      }
      // Clear errors and temporary states when modal opens
      setErrors([]);
      setNewTag('');
      setNewImage('');
      setImagesToDelete([]);
    }
  }, [isOpen, product, mode, cloneSource]);

  // Load shipping tiers when modal opens
  useEffect(() => {
    const loadShippingTiers = async () => {
      if (!isOpen) return;
      
      try {
        setLoadingShippingTiers(true);
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../services/firebase');
        
        const configDoc = await getDoc(doc(db, 'shippingConfig', 'main'));
        if (configDoc.exists()) {
          const data = configDoc.data();
          const tiers = data.tiers
            .filter((tier: any) => tier.active)
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
            .map((tier: any) => ({
              id: tier.id,
              name: tier.name,
              description: tier.description
            }));
          setAvailableShippingTiers(tiers);
          
          // If adding a new product, default to all shipping options
          if (mode === 'add' && formData.availableShippingOptions.length === 0) {
            setFormData(prev => ({
              ...prev,
              availableShippingOptions: tiers.map((tier: any) => tier.id)
            }));
          }
        }
      } catch (error) {
        console.error('Error loading shipping tiers:', error);
        toast.error('Failed to load shipping options');
      } finally {
        setLoadingShippingTiers(false);
      }
    };

    loadShippingTiers();
  }, [isOpen, mode]);

  // Reset form data when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to ensure modal is fully closed before resetting
      const timeoutId = setTimeout(() => {
        setFormData(defaultFormData);
        setOriginalImages([]);
        setErrors([]);
        setNewTag('');
        setNewImage('');
        setImagesToDelete([]);
        setAvailableShippingTiers([]);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value,
      };
      
      // Clear sale price when regular price is set to 0
      if (field === 'price' && value === 0) {
        newData.onSalePrice = null;
        newData.onSale = false;
      }
      
      return newData;
    });
  };

  const handleCommodityCodeChange = (country: 'US' | 'UK' | 'CA' | 'EU', value: string) => {
    setFormData(prev => ({
      ...prev,
      commodityCodes: {
        ...(prev.commodityCodes ?? defaultFormData.commodityCodes),
        [country]: value,
      },
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const addImage = () => {
    if (newImage.trim() && !formData.images.includes(newImage.trim())) {
      const updatedImages = [...formData.images, newImage.trim()];
      setFormData(prev => ({
        ...prev,
        images: updatedImages,
        image: prev.image || newImage.trim(), // Set as primary if no primary image
      }));
      setNewImage('');
    }
  };

  const removeImage = (imageToRemove: string) => {
    const updatedImages = formData.images.filter(img => img !== imageToRemove);
    setFormData(prev => ({
      ...prev,
      images: updatedImages,
      image: prev.image === imageToRemove ? (updatedImages[0] || '') : prev.image,
    }));
  };

  const setPrimaryImage = (image: string) => {
    setFormData(prev => ({
      ...prev,
      image: image,
    }));
  };

  const toggleShippingOption = (optionId: string) => {
    setFormData(prev => {
      const currentOptions = prev.availableShippingOptions;
      const isCurrentlySelected = currentOptions.includes(optionId);
      
      if (isCurrentlySelected) {
        // Remove the option (but don't allow removing all options)
        const newOptions = currentOptions.filter(id => id !== optionId);
        if (newOptions.length === 0) {
          // Don't allow removing all options
          toast.error('At least one shipping option must be selected');
          return prev;
        }
        return {
          ...prev,
          availableShippingOptions: newOptions,
        };
      } else {
        // Add the option
        return {
          ...prev,
          availableShippingOptions: [...currentOptions, optionId],
        };
      }
    });
  };

  const handleVariationsChange = (variations: ProductVariation[]) => {
    setFormData(prev => ({
      ...prev,
      variations,
    }));
  };

  const handleImagesChange = (images: string[]) => {
    setFormData(prev => ({
      ...prev,
      images: images,
      // Update primary image if it's not in the new images list
      image: images.includes(prev.image) ? prev.image : (images[0] || ''),
    }));
  };

  const handlePrimaryImageChange = (primaryImage: string) => {
    setFormData(prev => ({
      ...prev,
      image: primaryImage,
    }));
  };

  const handleImageMarkedForDeletion = (imageUrl: string) => {
    // Only track Firebase Storage images for deletion
    if (imageUrl.includes('firebasestorage.googleapis.com')) {
      setImagesToDelete(prev => {
        if (!prev.includes(imageUrl)) {
          return [...prev, imageUrl];
        }
        return prev;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      // Validate form data
      const validationErrors = validateProductData(formData);
      
      // Add shipping validation
      if (formData.availableShippingOptions.length === 0) {
        validationErrors.push('At least one shipping option must be selected');
      }
      
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        // Auto-scroll to top of form when there are validation errors
        if (formRef.current) {
          formRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
        return;
      }

      let updatedProduct: Product | undefined;
      let productId: string;

      if (mode === 'add') {
        const commodityCodesPayload = Object.entries(formData.commodityCodes ?? {}).reduce<Record<string, string>>(
          (acc, [key, value]) => {
            const trimmed = value.trim();
            if (!trimmed) return acc;
            acc[key.toUpperCase()] = trimmed;
            return acc;
          },
          {}
        );

        const { commodityCodes: _commodityCodes, ...formDataWithoutCommodityCodes } = formData;

        // Create the product first to get an ID
        productId = await createProduct({
          ...formDataWithoutCommodityCodes,
          ...(Object.keys(commodityCodesPayload).length > 0 ? { commodityCodes: commodityCodesPayload } : {}),
        } as any);
        
        // Move images from temp to product folder
        if (formData.images.length > 0) {
          const finalImageUrls = await moveImagesToProduct(formData.images, productId);
          const finalPrimaryImage = finalImageUrls.includes(formData.image) 
            ? formData.image 
            : finalImageUrls[0] || '';
          
          // Update product with final image URLs
          await updateProduct(productId, {
            images: finalImageUrls,
            image: finalPrimaryImage
          });
        }
        
        onSuccess();
      } else if (mode === 'edit' && product) {
        productId = product.id;
        const commodityCodesPayload = Object.entries(formData.commodityCodes ?? {}).reduce<Record<string, string>>(
          (acc, [key, value]) => {
            const trimmed = value.trim();
            if (!trimmed) return acc;
            acc[key.toUpperCase()] = trimmed;
            return acc;
          },
          {}
        );

        const { commodityCodes: _commodityCodes, ...formDataWithoutCommodityCodes } = formData;
        
        // Handle image changes for editing
        const removedImages = originalImages.filter(img => !formData.images.includes(img));
        
        // Combine removedImages with imagesToDelete for comprehensive cleanup
        const allImagesToDelete = [...new Set([...removedImages, ...imagesToDelete])];
        
        // Delete removed images from Firebase Storage
        if (allImagesToDelete.length > 0) {
          await deleteImagesByUrls(allImagesToDelete);
        }
        
        // Move any new temp images to product folder
        const finalImageUrls = await moveImagesToProduct(formData.images, productId);
        const finalPrimaryImage = finalImageUrls.includes(formData.image) 
          ? formData.image 
          : finalImageUrls[0] || '';
        
        // Update product data with final image URLs
        const updateData = {
          ...formDataWithoutCommodityCodes,
          images: finalImageUrls,
          image: finalPrimaryImage,
          // If user clears all fields, save an empty object to effectively clear existing codes
          commodityCodes: commodityCodesPayload,
        };
        
        await updateProduct(productId, updateData);
        
        // Create the updated product object for cache update
        updatedProduct = {
          ...product,
          ...updateData,
          updatedAt: new Date().toISOString(),
        };
        
        onSuccess(updatedProduct);
      }

      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
      setErrors(['Failed to save product. Please try again.']);
      toast.error('Failed to save product. Please try again.');
      // Auto-scroll to top of form when there are server errors
      if (formRef.current) {
        formRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      {mode === 'add' ? 'Create Product' : 'Edit Product'}
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      {mode === 'add' 
                        ? 'Add a new product to your catalog' 
                        : `Update ${product?.name || 'product'} details`
                      }
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-gray-50/50 to-white">
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
                {/* Error Display */}
                {errors.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      <h4 className="font-medium text-red-800">Please fix the following errors:</h4>
                    </div>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Basic Information */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card className="shadow-sm border-gray-200 h-fit">
                      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Package className="h-4 text-purple-600" />
                          Basic Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Product Name *
                          </label>
                          <Input
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter product name"
                            className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Description *
                          </label>
                          <Textarea
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Enter product description"
                            rows={4}
                            className="rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200 resize-none"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Category
                          </label>
                          <CategorySelect
                            value={formData.category}
                            onChange={(category) => handleInputChange('category', category)}
                            placeholder="Select or create category"
                            className="focus:ring-purple-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Brand
                          </label>
                          <BrandSelect
                            value={formData.brand}
                            onChange={(brand) => handleInputChange('brand', brand)}
                            placeholder="Select or create brand"
                            className="focus:ring-purple-600"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Pricing */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Card className="shadow-sm border-gray-200 h-fit">
                      <CardHeader className={`rounded-t-xl ${
                        formData.price === 0 
                          ? 'bg-gradient-to-r from-green-50 to-green-100/50' 
                          : 'bg-gradient-to-r from-gray-50 to-gray-100/50'
                      }`}>
                        <CardTitle className="flex items-center gap-2 text-base">
                          <DollarSign className={`h-4 w-4 ${
                            formData.price === 0 ? 'text-green-600' : 'text-purple-600'
                          }`} />
                          Pricing
                          {formData.price === 0 && (
                            <Badge className="bg-green-100 text-green-800 border-green-200 ml-2">
                              FREE PRODUCT
                            </Badge>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Regular Price * ($)
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.price}
                            onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Set to $0.00 for free products (useful for rewards and promotions)
                          </p>
                          {formData.price === 0 && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                              <CheckCircle2 className="h-4 w-4" />
                              <span>Free product - perfect for rewards and giveaways!</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-3 ${
                            formData.price === 0 ? 'text-gray-400' : 'text-gray-900'
                          }`}>
                            Sale Price ($)
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.onSalePrice || ''}
                            onChange={(e) => handleInputChange('onSalePrice', parseFloat(e.target.value) || null)}
                            placeholder="0.00"
                            disabled={formData.price === 0}
                            className={`h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200 ${
                              formData.price === 0 ? 'bg-gray-50 cursor-not-allowed' : ''
                            }`}
                          />
                          {formData.price === 0 && (
                            <p className="text-xs text-gray-500 mt-2">
                              Sale pricing is not available for free products
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Jamaican Price (JMD)
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.jamaicanPrice !== null ? formData.jamaicanPrice : ''}
                            onChange={(e) => handleInputChange('jamaicanPrice', e.target.value ? parseFloat(e.target.value) : null)}
                            placeholder="0.00"
                            className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Internal reference only. This does not affect store pricing.
                          </p>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                          <div>
                            <label className="text-sm font-medium text-gray-900">On Sale</label>
                            <p className="text-xs text-gray-600 mt-1">Mark product as on sale</p>
                          </div>
                          <Switch
                            checked={formData.onSale}
                            onCheckedChange={(checked) => handleInputChange('onSale', checked)}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* Images Section */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center justify-between text-base">
                        <div className="flex items-center gap-2">
                          <ImageIcon className="h-4 w-4 text-purple-600" />
                          Product Images
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`px-3 py-1 text-sm font-medium transition-all duration-300 ${
                            formData.images.length >= 8 
                              ? 'bg-yellow-50 text-yellow-700 border-yellow-300' 
                              : formData.images.length > 0 
                                ? 'bg-purple-50 text-purple-700 border-purple-300'
                                : 'bg-gray-50 text-gray-500 border-gray-300'
                          }`}
                        >
                          <ImageIcon className="w-3 h-3 mr-1" />
                          {formData.images.length}/8 Images
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <Upload className="w-4 h-4 text-purple-600" />
                          Upload Images
                        </h4>
                        <ImageUpload
                          existingImages={formData.images}
                          onImagesChange={handleImagesChange}
                          onPrimaryImageChange={handlePrimaryImageChange}
                          primaryImage={formData.image}
                          maxImages={8}
                          onImageMarkedForDeletion={handleImageMarkedForDeletion}
                        />
                      </div>

                      <div className="border-t pt-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-purple-600" />
                          Add Image URL
                        </h4>
                        <div className="flex gap-2">
                          <Input
                            value={newImage}
                            onChange={(e) => setNewImage(e.target.value)}
                            placeholder="https://example.com/image.jpg"
                            className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                            disabled={formData.images.length >= 8}
                          />
                          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                              type="button"
                              onClick={addImage}
                              variant="outline"
                              size="sm"
                              disabled={formData.images.length >= 8 || !newImage.trim()}
                              className="h-12 px-4 rounded-xl border-purple-200 hover:bg-purple-600 hover:text-white transition-all duration-300"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </motion.div>
                        </div>
                        {formData.images.length >= 8 && (
                          <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Maximum image limit reached
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Inventory */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Card className="shadow-sm border-gray-200 h-fit">
                      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Warehouse className="h-4 w-4 text-purple-600" />
                          Inventory
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Stock Quantity
                          </label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.stockQuantity}
                            onChange={(e) => handleInputChange('stockQuantity', parseInt(e.target.value) || 0)}
                            placeholder="0"
                            className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Low Stock Threshold
                          </label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.lowStockThreshold}
                            onChange={(e) => handleInputChange('lowStockThreshold', parseInt(e.target.value) || 0)}
                            placeholder="10"
                            className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Net Weight (Optional)
                          </label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formData.netWeight !== null ? formData.netWeight : ''}
                              onChange={(e) => handleInputChange('netWeight', e.target.value ? parseFloat(e.target.value) : null)}
                              placeholder="0.00"
                              className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                            />
                            <select
                              value={formData.weightUnit}
                              onChange={(e) => handleInputChange('weightUnit', e.target.value as 'g' | 'kg' | 'oz' | 'lb' | 'ml')}
                              className="h-12 px-3 rounded-xl border border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200 bg-white"
                            >
                              <option value="g">g</option>
                              <option value="kg">kg</option>
                              <option value="oz">oz</option>
                              <option value="lb">lb</option>
                              <option value="ml">ml</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Listing Quantity
                          </label>
                          <Input
                            type="number"
                            min="1"
                            value={formData.listingQuantity}
                            onChange={(e) => handleInputChange('listingQuantity', Math.max(1, parseInt(e.target.value) || 1))}
                            placeholder="1"
                            className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Number of items included in this product listing
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Tags */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <Card className="shadow-sm border-gray-200 h-fit">
                      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Tag className="h-4 w-4 text-purple-600" />
                          Tags
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            Add Tag
                          </label>
                          <div className="flex gap-2">
                            <Input
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              placeholder="Enter tag"
                              className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            />
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                              <Button
                                type="button"
                                onClick={addTag}
                                variant="outline"
                                size="sm"
                                disabled={!newTag.trim()}
                                className="h-12 px-4 rounded-xl border-purple-200 hover:bg-purple-600 hover:text-white transition-all duration-300"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </motion.div>
                          </div>
                        </div>

                        {formData.tags.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-3">
                              Current Tags
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {formData.tags.map((tag, index) => (
                                <Badge
                                  key={index}
                                  variant="outline"
                                  className="px-3 py-1 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 transition-all duration-200 cursor-pointer group"
                                  onClick={() => removeTag(tag)}
                                >
                                  {tag}
                                  <X className="w-3 h-3 ml-1 opacity-50 group-hover:opacity-100" />
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* Shipping Options */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Truck className="h-4 w-4 text-purple-600" />
                        Shipping Options
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {loadingShippingTiers ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                          <span className="ml-2 text-gray-600">Loading shipping options...</span>
                        </div>
                      ) : availableShippingTiers.length === 0 ? (
                        <div className="text-center py-8">
                          <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                          <p className="text-gray-600">No shipping options available</p>
                          <p className="text-sm text-gray-500">Configure shipping tiers in settings</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600 mb-4">
                            Select which shipping options are available for this product. At least one option must be selected.
                          </p>
                          <div className="grid grid-cols-1 gap-3">
                            {availableShippingTiers.map((tier) => {
                              const isSelected = formData.availableShippingOptions.includes(tier.id);
                              return (
                                <motion.div
                                  key={tier.id}
                                  whileHover={{ scale: 1.01 }}
                                  whileTap={{ scale: 0.99 }}
                                  onClick={() => toggleShippingOption(tier.id)}
                                  className={`
                                    flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all duration-200
                                    ${isSelected 
                                      ? 'border-purple-300 bg-purple-50 shadow-sm' 
                                      : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-25'
                                    }
                                  `}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`
                                      flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200
                                      ${isSelected 
                                        ? 'border-purple-500 bg-purple-500' 
                                        : 'border-gray-300 bg-white'
                                      }
                                    `}>
                                      {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-gray-900">{tier.name}</h4>
                                      <p className="text-sm text-gray-600">{tier.description}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-600">Available</span>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                          {formData.availableShippingOptions.length === 0 && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm text-red-600 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Please select at least one shipping option
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>

                {/* International Shipping & Customs */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.85 }}
                >
                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Globe className="h-4 w-4 text-blue-600" />
                        International Shipping & Customs
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            These fields are required for international shipping and automated label generation. 
                            They help calculate customs duties and ensure smooth border clearance.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAIBatchFill}
                            disabled={aiProductData.computed.isLoading || !formData.name.trim()}
                            className="ml-4 text-purple-600 border-purple-300 hover:bg-purple-50 flex items-center gap-2"
                          >
                            {aiProductData.computed.isLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4" />
                            )}
                            Auto-fill All
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <label className="block text-sm font-medium text-gray-900">
                                Manufacturer
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleAIManufacturer}
                                disabled={aiProductData.state.manufacturer.loading || !formData.name.trim()}
                                className="h-8 px-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors group"
                                title="Auto-populate with AI"
                              >
                                {aiProductData.state.manufacturer.loading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Sparkles className="h-4 w-4 group-hover:scale-110 transition-transform" />
                                )}
                              </Button>
                            </div>
                            <Input
                              value={formData.manufacturer}
                              onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                              placeholder="e.g., Grace Foods, Walkerswood"
                              className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                            />
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-xs text-gray-500">
                                Company or brand that manufactured this product
                              </p>
                              {aiProductData.state.manufacturer.data && (
                                <span className={`text-xs font-medium ${aiProductData.utils.getConfidenceColor(aiProductData.state.manufacturer.data.confidence)}`}>
                                  {aiProductData.state.manufacturer.data.confidence}% confidence
                                </span>
                              )}
                            </div>
                            {aiProductData.state.manufacturer.error && (
                              <p className="text-xs text-red-600 mt-1">
                                AI Error: {aiProductData.state.manufacturer.error}
                              </p>
                            )}
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <label className="block text-sm font-medium text-gray-900">
                                Origin Country
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleAIOriginCountry}
                                disabled={aiProductData.state.originCountry.loading || !formData.name.trim()}
                                className="h-8 px-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors group"
                                title="Auto-populate with AI"
                              >
                                {aiProductData.state.originCountry.loading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Sparkles className="h-4 w-4 group-hover:scale-110 transition-transform" />
                                )}
                              </Button>
                            </div>
                            <Input
                              value={formData.originCountry}
                              onChange={(e) => handleInputChange('originCountry', e.target.value.toUpperCase())}
                              placeholder="e.g., JM, US, CA"
                              maxLength={2}
                              className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                            />
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-xs text-gray-500">
                                Two-character country code where product was made
                              </p>
                              {aiProductData.state.originCountry.data && (
                                <span className={`text-xs font-medium ${aiProductData.utils.getConfidenceColor(aiProductData.state.originCountry.data.confidence)}`}>
                                  {aiProductData.state.originCountry.data.confidence}% confidence
                                </span>
                              )}
                            </div>
                            {aiProductData.state.originCountry.error && (
                              <p className="text-xs text-red-600 mt-1">
                                AI Error: {aiProductData.state.originCountry.error}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-medium text-gray-900">
                              HS Code (Harmonized System Code)
                            </label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleAIHSCode}
                              disabled={aiProductData.state.hsCode.loading || !formData.name.trim()}
                              className="h-8 px-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors group"
                              title="Auto-populate with AI"
                            >
                              {aiProductData.state.hsCode.loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Sparkles className="h-4 w-4 group-hover:scale-110 transition-transform" />
                              )}
                            </Button>
                          </div>
                          <Input
                            value={formData.hsCode}
                            onChange={(e) => handleInputChange('hsCode', e.target.value)}
                            placeholder="e.g., 210390 (for spices and seasonings)"
                            className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-500">
                              6-10 digit international trade classification code. 
                              <a 
                                href="https://hts.usitc.gov/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 ml-1"
                              >
                                Look up HS codes here
                              </a>
                            </p>
                            {aiProductData.state.hsCode.data && (
                              <span className={`text-xs font-medium ${aiProductData.utils.getConfidenceColor(aiProductData.state.hsCode.data.confidence)}`}>
                                {aiProductData.state.hsCode.data.confidence}% confidence
                              </span>
                            )}
                          </div>
                          {aiProductData.state.hsCode.error && (
                            <p className="text-xs text-red-600 mt-1">
                              AI Error: {aiProductData.state.hsCode.error}
                            </p>
                          )}
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-900">
                              Commodity Codes
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Optional destination-specific commodity codes (leave blank if not available).
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">US</label>
                              <Input
                                value={formData.commodityCodes?.US ?? ''}
                                onChange={(e) => handleCommodityCodeChange('US', e.target.value)}
                                placeholder="##########"
                                className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">UK</label>
                              <Input
                                value={formData.commodityCodes?.UK ?? ''}
                                onChange={(e) => handleCommodityCodeChange('UK', e.target.value)}
                                placeholder="##########"
                                className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">CA</label>
                              <Input
                                value={formData.commodityCodes?.CA ?? ''}
                                onChange={(e) => handleCommodityCodeChange('CA', e.target.value)}
                                placeholder="##########"
                                className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">EU</label>
                              <Input
                                value={formData.commodityCodes?.EU ?? ''}
                                onChange={(e) => handleCommodityCodeChange('EU', e.target.value)}
                                placeholder="########"
                                className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                              />
                            </div>
                          </div>
                        </div>

                        {/* AI Confidence Summary */}
                        {aiProductData.computed.hasAnyData && (
                          <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                            <div className="flex items-start gap-3">
                              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <h4 className="font-medium text-purple-800 mb-1">
                                  AI Auto-Population Summary
                                </h4>
                                <p className="text-sm text-purple-700">
                                  {(() => {
                                    const summary = aiProductData.computed.getConfidenceSummary();
                                    if (summary) {
                                      return `Average confidence: ${summary.average}% (${summary.description}). Please review and verify the auto-populated data.`;
                                    }
                                    return 'Some fields have been auto-populated with AI. Please review and verify the data.';
                                  })()}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {(!formData.manufacturer || !formData.hsCode || !formData.originCountry) && (
                          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <h4 className="font-medium text-amber-800 mb-1">
                                  International Shipping Notice
                                </h4>
                                <p className="text-sm text-amber-700">
                                  Without these customs fields, automatic label generation for international 
                                  shipments will not be available. You can still add tracking information manually.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Product Settings */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Settings className="h-4 w-4 text-purple-600" />
                        Product Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                          <div>
                            <label className="text-sm font-medium text-gray-900">New Product</label>
                            <p className="text-xs text-gray-600 mt-1">Mark as new arrival</p>
                          </div>
                          <Switch
                            checked={formData.isNew}
                            onCheckedChange={(checked) => handleInputChange('isNew', checked)}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                          <div>
                            <label className="text-sm font-medium text-gray-900">Best Seller</label>
                            <p className="text-xs text-gray-600 mt-1">Mark as best seller</p>
                          </div>
                          <Switch
                            checked={formData.isBestSeller}
                            onCheckedChange={(checked) => handleInputChange('isBestSeller', checked)}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                          <div>
                            <label className="text-sm font-medium text-gray-900">On Sale</label>
                            <p className="text-xs text-gray-600 mt-1">Currently discounted</p>
                          </div>
                          <Switch
                            checked={formData.onSale}
                            onCheckedChange={(checked) => handleInputChange('onSale', checked)}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                          <div>
                            <label className="text-sm font-medium text-gray-900">Disabled</label>
                            <p className="text-xs text-gray-600 mt-1">Hide from store</p>
                          </div>
                          <Switch
                            checked={formData.disabled}
                            onCheckedChange={(checked) => handleInputChange('disabled', checked)}
                            className="data-[state=checked]:bg-red-600"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Product Variations */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 }}
                >
                  <VariationsManager
                    variations={formData.variations}
                    onVariationsChange={handleVariationsChange}
                    currentProductId={product?.id}
                    currentProductName={formData.name || 'New Product'}
                  />
                </motion.div>
              </form>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-end gap-4">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={loading}
                    className="px-6 h-11 rounded-xl font-medium"
                  >
                    Cancel
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    form={formRef.current?.id}
                    onClick={(e) => {
                      e.preventDefault();
                      formRef.current?.requestSubmit();
                    }}
                    disabled={loading}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-purple-600/25"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {mode === 'add' ? 'Creating...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {mode === 'add' ? 'Create Product' : 'Save Changes'}
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
} 
